<template>
    <div ref="tagElement" v-bind="tagAttributes" v-on="tagEvents">
        <div class="Tag-content">
            <slot />
        </div>
        <div v-if="removeable" class="Tag-remove" @click.stop="remove">
            <i class="isax isax-add"></i>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from "vue";

const props = defineProps(
{
    id         : { type: [ String, Number ], default: Math.floor(Math.random() * Number.MAX_SAFE_INTEGER ) },
    removeable : { type: Boolean, default: true }
});

const emit = defineEmits([ 'on:remove' ]);

const tagElement = ref( null );

const tagAttributes = computed( () =>
{
    return (
    {
        "class":
        {
            'Tag': true
        }
    })
});

const tagEvents = computed( () =>
{
    return (
    {

    })
});

const remove = () =>
{
    emit('on:remove', { id: props.id, label: props.label } );
}
</script>

<style lang="scss">
body
{
    &[data-theme="light"]
    {
        .Tag.primary
        {
            --tag-text-color          : var(--primary-colors-blue-70);
            --tag-hover-text-color    : var(--primary-colors-blue-50);
            --tag-active-text-color   : var(--primary-colors-blue-60);
            --tag-focus-text-color    : var(--primary-colors-blue-60);
            --tag-disabled-text-color : var(--primary-colors-blue-50);

            --tag-success-text-color  : var(--accent-colors-green-50);
            --tag-warning-text-color  : var(--warning-colors-warning-50);
            --tag-error-text-color    : var(--error-colors-error-50);

            --tag-background-color          : var(--primary-colors-blue-20);
            --tag-hover-background-color    : var(--primary-colors-blue-20);
            --tag-active-background-color   : var(--primary-colors-blue-20);
            --tag-focus-background-color    : var(--primary-colors-blue-20);
            --tag-disabled-background-color : var(--primary-colors-blue-20);

            --tag-success-background-color  : var(--success-colors-success-10);
            --tag-warning-background-color  : var(--warning-colors-warning-10);
            --tag-error-background-color    : var(--error-colors-error-10);

            --tag-border-style          : solid;
            --tag-hover-border-style    : solid;
            --tag-active-border-style   : solid;
            --tag-focus-border-style    : dashed;
            --tag-disabled-border-style : solid;

            --tag-success-border-style   : solid;
            --tag-warning-border-style   : solid;
            --tag-error-border-style     : solid;

            --tag-border-width          : 1px;
            --tag-hover-border-width    : 1px;
            --tag-active-border-width   : 1px;
            --tag-focus-border-width    : 1px;
            --tag-disabled-border-width : 1px;

            --tag-success-border-width   : 1px;
            --tag-warning-border-width   : 1px;
            --tag-error-border-width     : 1px;

            --tag-border-color          : transparent;
            --tag-hover-border-color    : transparent;
            --tag-active-border-color   : var(--primary-colors-blue-60);
            --tag-focus-border-color    : var(--primary-colors-blue-60);
            --tag-disabled-border-color : transparent;

            --tag-success-border-color  : transparent;
            --tag-warning-border-color  : transparent;
            --tag-error-border-color    : transparent;
        }
    }

    &[data-theme="dark"]
    {
        .Tag.primary
        {
            --tag-text-color          : var(--primary-colors-blue-30);
            --tag-hover-text-color    : var(--primary-colors-blue-40);
            --tag-active-text-color   : var(--primary-colors-blue-50);
            --tag-focus-text-color    : var(--primary-colors-blue-50);
            --tag-disabled-text-color : var(--primary-colors-blue-30);

            --tag-success-text-color  : var(--accent-colors-green-20);
            --tag-warning-text-color  : var(--warning-colors-warning-20);
            --tag-error-text-color    : var(--error-colors-error-20);

            --tag-background-color          : var(--dark-surfaces-gunmetal);
            --tag-hover-background-color    : var(--dark-surfaces-baltic-sea);
            --tag-active-background-color   : var(--dark-surfaces-gunmetal);
            --tag-focus-background-color    : var(--dark-surfaces-gunmetal);
            --tag-disabled-background-color : var(--dark-surfaces-gunmetal);

            --tag-success-background-color  : var(--dark-surfaces-gunmetal);
            --tag-warning-background-color  : var(--dark-surfaces-gunmetal);
            --tag-error-background-color    : var(--dark-surfaces-gunmetal);

            --tag-border-style          : solid;
            --tag-hover-border-style    : solid;
            --tag-active-border-style   : solid;
            --tag-focus-border-style    : dashed;
            --tag-disabled-border-style : solid;

            --tag-success-border-style   : solid;
            --tag-warning-border-style   : solid;
            --tag-error-border-style     : solid;

            --tag-border-width          : 1px;
            --tag-hover-border-width    : 1px;
            --tag-active-border-width   : 1px;
            --tag-focus-border-width    : 1px;
            --tag-disabled-border-width : 1px;

            --tag-success-border-width   : 1px;
            --tag-warning-border-width   : 1px;
            --tag-error-border-width     : 1px;

            --tag-border-color          : var(--dark-surfaces-davy);
            --tag-hover-border-color    : var(--dark-surfaces-gunmetal);
            --tag-active-border-color   : var(--primary-colors-blue-50);
            --tag-focus-border-color    : var(--primary-colors-blue-50);
            --tag-disabled-border-color : transparent;

            --tag-success-border-color  : var(--dark-surfaces-davy);
            --tag-warning-border-color  : var(--dark-surfaces-davy);
            --tag-error-border-color    : var(--dark-surfaces-davy);
        }
    }
}

.Tag
{
    display: inline-flex;
    align-items: stretch;
    justify-content: center;
    flex-shrink: 0;

    gap: 1px;

    color: var(--tag-text-color);

    outline-style: var(--tag-border-style);
    outline-width: var(--tag-border-width);
    outline-color: var(--tag-border-color);
    border-radius: 4px;

    user-select: none;
    cursor: default;
    overflow: hidden;

    transition: all 250ms ease-in-out;

    .Tag-content
    {
        background-color: var(--tag-background-color);
    }

    .Tag-remove
    {
        background-color: var(--tag-background-color);

        cursor: pointer;

        > i
        {
            transform: rotate( 45deg );
        }
    }

    &.lg
    {
        width: fit-content;
        min-height: px(32);

        .Tag-content
        {
            display: flex;
            justify-content: center;
            align-items: center;

            font-size: fpx(14);
            line-height: px(20);
            font-weight: 500;
            font-style: normal;

            padding: px(6) px(12);

            > i
            {
                width: px(20);
                height: px(20);
                font-size: px(20);
            }
        }

        .Tag-remove
        {
            display: flex;
            justify-content: center;
            align-items: center;

            padding: px(6);

            > i
            {
                display: flex;
                align-items: center;
                justify-content: center;

                width: px(20);
                height: px(20);
                font-size: px(20);
            }
        }
    }

    &.md
    {
        width: fit-content;
        min-height: px(28);

        .Tag-content
        {
            display: flex;
            justify-content: center;
            align-items: center;

            gap: px(8);

            font-size: fpx(14);
            line-height: px(20);
            font-weight: 500;
            font-style: normal;

            padding: px(4) px(8);

            > i
            {
                width: px(20);
                height: px(20);
                font-size: px(20);
            }
        }

        .Tag-remove
        {
            display: flex;
            justify-content: center;
            align-items: center;

            padding: px(4);

            > i
            {
                display: flex;
                align-items: center;
                justify-content: center;

                width: px(20);
                height: px(20);
                font-size: px(20);
            }
        }
    }

    &.sm
    {
        width: fit-content;
        min-height: px(22);

        .Tag-content
        {
            display: flex;
            justify-content: center;
            align-items: center;

            gap: px(8);

            font-size: fpx(12);
            line-height: px(18);
            font-weight: 500;
            font-style: normal;

            padding: px(2) px(8);

            > i
            {
                width: px(18);
                height: px(18);
                font-size: px(18);
            }
        }

        .Tag-remove
        {
            display: flex;
            justify-content: center;
            align-items: center;

            padding: px(2);

            > i
            {
                display: flex;
                align-items: center;
                justify-content: center;

                width: px(18);
                height: px(18);
                font-size: px(18);
            }
        }
    }

    &.hovered,
    &:hover
    {
        color: var(--tag-hover-text-color);

        outline-style: var(--tag-hover-border-style);
        outline-width: var(--tag-hover-border-width);
        outline-color: var(--tag-hover-border-color);

        .Tag-content,
        .Tag-remove
        {
            background-color: var(--tag-hover-background-color);
        }
    }

    &.actived,
    &:active
    {
        color: var(--tag-active-text-color);

        outline-style: var(--tag-active-border-style);
        outline-width: var(--tag-active-border-width);
        outline-color: var(--tag-active-border-color);

        .Tag-content,
        .Tag-remove
        {
            background-color: var(--tag-active-background-color);
        }
    }

    &.focused,
    &:focus
    {
        color: var(--tag-focus-text-color);

        outline-style: var(--tag-focus-border-style);
        outline-width: var(--tag-focus-border-width);
        outline-color: var(--tag-focus-border-color);

        .Tag-content,
        .Tag-remove
        {
            background-color: var(--tag-focus-background-color);
        }
    }

    &.disabled,
    &:disabled
    {
        color: var(--tag-disabled-text-color);

        outline-style: var(--tag-disabled-border-style);
        outline-width: var(--tag-disabled-border-width);
        outline-color: var(--tag-disabled-border-color);

        opacity: 0.5;

        .Tag-content,
        .Tag-remove
        {
            background-color: var(--tag-disabled-background-color);

            cursor: default;
        }
    }

    &.success
    {
        color: var(--tag-success-text-color);

        outline-style: var(--tag-success-border-style);
        outline-width: var(--tag-success-border-width);
        outline-color: var(--tag-success-border-color);

        .Tag-content,
        .Tag-remove
        {
            background-color: var(--tag-success-background-color);
        }
    }

    &.warning
    {
        color: var(--tag-warning-text-color);

        outline-style: var(--tag-warning-border-style);
        outline-width: var(--tag-warning-border-width);
        outline-color: var(--tag-warning-border-color);

        .Tag-content,
        .Tag-remove
        {
            background-color: var(--tag-warning-background-color);
        }
    }

    &.error
    {
        color: var(--tag-error-text-color);

        outline-style: var(--tag-error-border-style);
        outline-width: var(--tag-error-border-width);
        outline-color: var(--tag-error-border-color);

        .Tag-content,
        .Tag-remove
        {
            background-color: var(--tag-error-background-color);
        }
    }
}
</style>